import { StyleSheet, Text, TouchableOpacity, View, Alert } from 'react-native';
import ImagePicker, { ImageOrVideo } from 'react-native-image-crop-picker';
import { BigCameraIcon } from '~assets/icons';
import handleGoToSettingsImage from '~Utils/Settings';
import storage from '@react-native-firebase/storage';
import { useUserStore } from '~providers/userStore/zustand';
import { useTheme } from '~contexts/ThemeContext';
import CircularImage from '~components/CircularImage';
import { spacing, typography } from '~constants/design';

interface IProps {
  isFromOnboarding?: boolean;
  errorText?: any;
  value?: string;
  onChange: (value: string) => void;
  name?: string;
  email?: string;
}

const ProfileImageComponent = ({ isFromOnboarding, errorText, value, onChange, name, email }: IProps) => {
  const { colors } = useTheme();

  const saveImageToDB = async (image: ImageOrVideo) => {
    try {
      if (!isFromOnboarding) {
        const user = useUserStore.getState().user;

        try {
          // Upload to Firebase Storage (now works without authentication)
          await storage().ref('users/' + user?.uid + '/profile.png').putFile(image.path);
          const url = await storage()
            .ref('users/' + user?.uid + '/profile.png')
            .getDownloadURL();

          onChange(url);
        } catch (error: any) {
          console.error('Profile picture upload failed:', error);
          Alert.alert(
            'Upload Failed',
            error.message || 'Failed to upload profile picture. Please try again.',
            [{ text: 'OK' }]
          );
        }
        return;
      }

      // For onboarding, just use the local path
      onChange(image.path);
    } catch (error) {
      console.error('Image processing error:', error);
      Alert.alert(
        'Error',
        'Failed to process image. Please try again.',
        [{ text: 'OK' }]
      );
    }
  };

  const selectImageFromGallery = async () => {
    try {
      const image = await ImagePicker.openPicker({
        width: 600,
        height: 600,
        cropping: true,
      });

      await saveImageToDB(image);
    } catch (error: any) {
      if (error.message && error.message !== 'User cancelled image selection') {
        handleGoToSettingsImage();
      }
    }
  };

  const modernStyles = StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: spacing.sm,
    },
    imageContainer: {
      position: 'relative',
    },
    infoContainer: {
      flex: 1,
      marginLeft: spacing.lg,
      justifyContent: 'center',
    },
    cameraButton: {
      position: 'absolute',
      bottom: 0,
      right: 0,
      backgroundColor: colors.primary,
      borderRadius: 12,
      width: 24,
      height: 24,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 2,
      borderColor: colors.background,
      elevation: 4,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.15,
      shadowRadius: 3,
    },
    nameText: {
      fontSize: 17,
      fontWeight: '700',
      color: colors.textPrimary,
      marginBottom: 1,
    },
    emailText: {
      fontSize: 12,
      fontWeight: '500',
      color: colors.textSecondary,
      letterSpacing: 0.1,
    },
    errorText: {
      fontSize: 11,
      fontWeight: '400',
      color: colors.error,
      marginTop: 4,
    },
  });

  return (
    <View style={modernStyles.container}>
      <View style={modernStyles.imageContainer}>
        <CircularImage
          source={
            value
              ? {
                uri: value,
                priority: 'high',
                cache: 'immutable',
              }
              : require('~assets/images/eventPhotoPlaceholder.png')
          }
          size={65}
          showShadow={false}
        />

        <TouchableOpacity 
          onPress={selectImageFromGallery} 
          style={modernStyles.cameraButton}
          activeOpacity={0.8}
        >
          <BigCameraIcon width={18} height={18} />
        </TouchableOpacity>
      </View>

      <View style={modernStyles.infoContainer}>
        {!isFromOnboarding && (
          <>
            <Text numberOfLines={1} style={modernStyles.nameText}>{name}</Text>
            <Text numberOfLines={1} style={modernStyles.emailText}>{email}</Text>
          </>
        )}
        {errorText && <Text style={modernStyles.errorText}>{errorText}</Text>}
      </View>
    </View>
  );
};

export default ProfileImageComponent;
